import { useState } from 'react';
import { InvoiceActivity } from '@/types/firebase';
import { 
  Clock, 
  Send, 
  Eye, 
  CreditCard, 
  AlertTriangle, 
  FileText, 
  XCircle, 
  PenTool,
  CalendarCheck,
  ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';

interface InvoiceActivityTimelineProps {
  activities: InvoiceActivity[];
  className?: string;
}

export function InvoiceActivityTimeline({ activities, className }: InvoiceActivityTimelineProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!activities || activities.length === 0) {
    return (
      <div className={cn("text-center p-6 text-gray-500", className)}>
        No activity recorded for this invoice yet.
      </div>
    );
  }

  // Sort activities by timestamp (most recent first)
  const sortedActivities = [...activities].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // If collapsed, only show the most recent activity
  const displayedActivities = isExpanded ? sortedActivities : sortedActivities.slice(0, 1);

  const getActivityIcon = (type: InvoiceActivity['type']) => {
    switch (type) {
      case 'created':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'sent':
        return <Send className="h-5 w-5 text-green-500" />;
      case 'viewed':
        return <Eye className="h-5 w-5 text-purple-500" />;
      case 'paid':
        return <CreditCard className="h-5 w-5 text-emerald-500" />;
      case 'overdue':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'edited':
        return <PenTool className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getActivityColor = (type: InvoiceActivity['type']) => {
    switch (type) {
      case 'created':
        return 'bg-blue-100 border-blue-200';
      case 'sent':
        return 'bg-green-100 border-green-200';
      case 'viewed':
        return 'bg-purple-100 border-purple-200';
      case 'paid':
        return 'bg-emerald-100 border-emerald-200';
      case 'overdue':
        return 'bg-amber-100 border-amber-200';
      case 'cancelled':
        return 'bg-red-100 border-red-200';
      case 'edited':
        return 'bg-gray-100 border-gray-200';
      default:
        return 'bg-gray-50 border-gray-100';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }).format(date);
  };

  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Invoice Timeline</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 h-8 w-8"
          aria-label={isExpanded ? "Collapse timeline" : "Expand timeline"}
        >
          <ChevronDown 
            className={cn(
              "h-5 w-5 text-gray-500 transition-transform duration-200",
              isExpanded ? "transform rotate-180" : ""
            )} 
          />
        </Button>
      </div>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200 ml-px" />

        {/* Activity items */}
        <div 
          className={cn(
            "space-y-6 pt-1 pb-4 transition-all duration-300",
            !isExpanded && sortedActivities.length > 1 && "relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-8 after:bg-gradient-to-t after:from-white after:to-transparent after:pointer-events-none"
          )}
        >
          {displayedActivities.map((activity) => (
            <div key={activity.id} className="relative flex items-start">
              {/* Icon with background */}
              <div className="flex-shrink-0 z-10">
                <div className={cn(
                  "w-8 h-8 flex items-center justify-center rounded-full border-2",
                  getActivityColor(activity.type)
                )}>
                  {getActivityIcon(activity.type)}
                </div>
              </div>

              {/* Content */}
              <div className="ml-3 flex-grow">
                <div className="flex flex-wrap items-baseline justify-between">
                  <div className="text-sm font-medium text-gray-900">
                    {activity.description}
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap flex items-center">
                    <CalendarCheck className="mr-1 h-3.5 w-3.5" />
                    {formatTime(activity.timestamp)}
                  </div>
                </div>
                
                {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                  <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    {Object.entries(activity.metadata).map(([key, value]) => (
                      <div key={key} className="flex">
                        <span className="font-medium mr-1">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                        <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Show toggle text if there are more activities */}
        {!isExpanded && sortedActivities.length > 1 && (
          <div className="text-center mt-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsExpanded(true)}
              className="text-xs text-blue-500 hover:text-blue-700"
            >
              Show {sortedActivities.length - 1} more {sortedActivities.length - 1 === 1 ? 'activity' : 'activities'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 